import React, { useMemo } from 'react';
import Select, { SingleValue } from 'react-select';
import { useDarkMode } from '../../hooks/useDarkMode';
import { getSelectStyles } from './selectStyles';

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isClearable?: boolean;
  variant?: 'default' | 'compact';
  className?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  isClearable = true,
  variant = 'default',
  className = '',
}) => {
  const { isDarkMode } = useDarkMode();

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) || null,
    [options, value]
  );

  const customStyles = useMemo(
    () => getSelectStyles<Option>(isDarkMode, variant),
    [isDarkMode, variant]
  );

  const handleChange = (newValue: SingleValue<Option>) => {
    if (newValue) {
      onChange(newValue.value);
    } else {
      onChange('');
    }
  };

  return (
    <Select<Option, false>
      options={options}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable={isClearable}
      isSearchable
      noOptionsMessage={() => 'No options found'}
      styles={customStyles}
      className={`react-select-container ${className}`}
      classNamePrefix="react-select"
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuPlacement="auto"
    />
  );
};
